import type { MoveInput, SimState, Vec3, WorldData } from './types';
import type { Simulation } from './simulation';
export type DemoRoute = {
    id: string;
    initialState: number[];
    inputs: number[][];
    states: number[][];
};
export type Demonstrations = {
    physicsVersion: number;
    routes: DemoRoute[];
};
/** Replays tested inputs through the shipping solver, then physically walks home. */
export class DemoPilot {
    phase: 'waiting' | 'crossing' | 'returning' | 'settling' = 'waiting';
    frame = 0;
    loops = 0;
    failures = 0;
    private waypoint = 0;
    private delay = 0;
    private home: Vec3;
    private returning: Vec3[];
    constructor(private sim: Simulation, public slot: number, private route: DemoRoute, world: WorldData, delay = 0) {
        this.home = route.initialState.slice(0, 3) as Vec3;
        const bank = world.routes.find(r => r.id === 'ravine-return')!;
        this.returning = [[0, 14.65, 27], ...bank.points.slice().reverse(), [0, 14.65, 0], this.home];
        this.reset(delay);
    }
    reset(delay = 0) { this.sim.restore(this.route.initialState, this.slot); this.phase = 'waiting'; this.delay = delay; this.frame = 0; this.waypoint = 0; }
    step(player: SimState, waitForPlayer = false) {
        let s = this.sim.read(this.slot);
        let input: MoveInput = { x: 0, z: 0, buttons: 0, assist: 1 };
        if (this.phase === 'waiting') {
            this.delay -= 1 / 120;
            if (this.delay <= 0 && (!waitForPlayer || Math.hypot(player.p[0] - s.p[0], player.p[2] - s.p[2]) < 10)) {
                this.phase = 'crossing';
                this.frame = 0;
                // Settled on the start mark (within a few centimetres): resume from the recorded
                // state so timers/latches match the tape and the replay stays exact.
                if (Math.hypot(...s.p.map((n, i) => n - this.home[i])) < .05 && s.mode === 0)
                    { this.sim.restore(this.route.initialState, this.slot); s = this.sim.read(this.slot); }
            }
        }
        if (this.phase === 'crossing') {
            const a = this.route.inputs[this.frame], desired = this.frame ? this.route.states[this.frame - 1] : this.route.initialState;
            if (a) {
                const x = a[0] + (desired[0] - s.p[0]) * .15, z = a[1] + (desired[2] - s.p[2]) * .15, d = Math.max(1, Math.hypot(x, z));
                input = { x: x / d, z: z / d, buttons: a[2], assist: a[3] };
                this.frame++;
            }
            else if (s.mode === 0) {
                this.loops++;
                this.phase = 'returning';
                this.waypoint = 0;
            }
            else
                input = { x: -s.p[0] * .2, z: (27 - s.p[2]) * .2, buttons: 1, assist: 1 };
        }
        if (this.phase === 'returning' || this.phase === 'settling') {
            const goal = this.phase === 'settling' ? this.home : this.returning[this.waypoint];
            const dx = goal[0] - s.p[0], dz = goal[2] - s.p[2], d = Math.hypot(dx, dz);
            const wait = waitForPlayer && this.waypoint === 0 && Math.hypot(player.p[0] - s.p[0], player.p[2] - s.p[2]) > 9;
            if (!wait) {
                input = { x: dx * 2 / Math.max(1, d * 2), z: dz * 2 / Math.max(1, d * 2), buttons: 0, assist: 1 };
                if (this.phase === 'returning' && d < .28) {
                    this.waypoint++;
                    if (this.waypoint >= this.returning.length)
                        this.phase = 'settling';
                }
                if (this.phase === 'settling' && d < .025 && Math.hypot(...s.v) < .1) {
                    this.phase = 'waiting';
                    this.delay = 1;
                }
            }
        }
        this.sim.step(this.slot, input);
        if (this.sim.read(this.slot).p[1] < 8 && s.p[1] >= 8)
            this.failures++;
    }
}
